import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// Note: Value adjustment thunks are exported below:
// - createValueAdjustmentFull: Create value adjustment (cost changes without quantity changes)
// - updateValueAdjustmentFull: Update value adjustment

// Get list of item adjustments with filters and pagination
export const getItemAdjustments = createAsyncThunk(
  "itemAdjustment/getItemAdjustments",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.STOCK_ADJUSTMENT.SLUG}?${queryString}`,
      );
      const list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Get adjustment reasons for dropdown
export const getAdjustmentReasons = createAsyncThunk(
  "itemAdjustment/getAdjustmentReasons",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.STOCK_ADJUSTMENT.REASONS);
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Get item adjustment by ID with all items
export const getItemAdjustmentById = createAsyncThunk(
  "itemAdjustment/getItemAdjustmentById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.STOCK_ADJUSTMENT.SLUG}/${id}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Create new item adjustment
export const createItemAdjustment = createAsyncThunk(
  "itemAdjustment/createItemAdjustment",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.STOCK_ADJUSTMENT.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Update item adjustment header
export const updateItemAdjustment = createAsyncThunk(
  "itemAdjustment/updateItemAdjustment",
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.STOCK_ADJUSTMENT.SLUG}/${id}`,
        payload,
      );
      dispatch(getItemAdjustmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Add or update an item
export const addOrUpdateItem = createAsyncThunk(
  "itemAdjustment/addOrUpdateItem",
  async ({ adjustmentId, payload }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(
        API_URL.STOCK_ADJUSTMENT.ITEMS(adjustmentId),
        payload,
      );
      dispatch(getItemAdjustmentById(adjustmentId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Delete an item
export const deleteItem = createAsyncThunk(
  "itemAdjustment/deleteItem",
  async ({ adjustmentId, itemId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(
        API_URL.STOCK_ADJUSTMENT.DELETE_ITEM(adjustmentId, itemId),
      );
      dispatch(getItemAdjustmentById(adjustmentId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Submit for approval
export const submitItemAdjustment = createAsyncThunk(
  "itemAdjustment/submitItemAdjustment",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.STOCK_ADJUSTMENT.SUBMIT(id));
      dispatch(getItemAdjustmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Approve item adjustment
export const approveItemAdjustment = createAsyncThunk(
  "itemAdjustment/approveItemAdjustment",
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(
        API_URL.STOCK_ADJUSTMENT.APPROVE(id),
        payload || {},
      );
      dispatch(getItemAdjustmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Reject item adjustment
export const rejectItemAdjustment = createAsyncThunk(
  "itemAdjustment/rejectItemAdjustment",
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(
        API_URL.STOCK_ADJUSTMENT.REJECT(id),
        payload,
      );
      dispatch(getItemAdjustmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Cancel item adjustment
export const cancelItemAdjustment = createAsyncThunk(
  "itemAdjustment/cancelItemAdjustment",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.STOCK_ADJUSTMENT.CANCEL(id));
      dispatch(getItemAdjustmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Post item adjustment
export const postItemAdjustment = createAsyncThunk(
  "itemAdjustment/postItemAdjustment",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.STOCK_ADJUSTMENT.POST(id));
      dispatch(getItemAdjustmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Get next adjustment number
export const getNextAdjustmentNumber = createAsyncThunk(
  "itemAdjustment/getNextAdjustmentNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.STOCK_ADJUSTMENT.GET_NEXT_ADJUSTMENT_NUMBER,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Create item adjustment with header + items in single call (composite API)
export const createItemAdjustmentFull = createAsyncThunk(
  "itemAdjustment/createItemAdjustmentFull",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.STOCK_ADJUSTMENT.FULL, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Update item adjustment with header + items in single call (composite API)
export const updateItemAdjustmentFull = createAsyncThunk(
  "itemAdjustment/updateItemAdjustmentFull",
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(
        API_URL.STOCK_ADJUSTMENT.FULL_UPDATE(id),
        payload,
      );
      dispatch(getItemAdjustmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Create value adjustment with header + items in single call (composite API)
// Value adjustments change inventory cost WITHOUT changing quantity
export const createValueAdjustmentFull = createAsyncThunk(
  "itemAdjustment/createValueAdjustmentFull",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.STOCK_ADJUSTMENT.VALUE_FULL,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Update value adjustment with header + items in single call (composite API)
export const updateValueAdjustmentFull = createAsyncThunk(
  "itemAdjustment/updateValueAdjustmentFull",
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(
        API_URL.STOCK_ADJUSTMENT.VALUE_FULL_UPDATE(id),
        payload,
      );
      dispatch(getItemAdjustmentById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Get active locations for dropdown
export const getActiveLocations = createAsyncThunk(
  "itemAdjustment/getActiveLocations",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.LOCATION.ACTIVE_LOCATIONS_LOOKUP);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Get warehouses by location
export const getWarehousesByLocation = createAsyncThunk(
  "itemAdjustment/getWarehousesByLocation",
  async (locationId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.WAREHOUSE.LOOKUP}?LocationId=${locationId}&IsActive=true`,
      );
      return data.data?.list || data.data || [];
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Get bins by warehouse — supports paginated + searchable dropdown.
// Payload can be either a plain warehouseId string (legacy non-paginated) or
// { warehouseId, PageNumber, PageSize, searchText } for paginated requests.
export const getBinsByWarehouse = createAsyncThunk(
  "itemAdjustment/getBinsByWarehouse",
  async (payload, { rejectWithValue }) => {
    try {
      const isObjectPayload = payload && typeof payload === "object";
      const warehouseId = isObjectPayload ? payload.warehouseId : payload;
      const pageNumber = isObjectPayload ? payload.PageNumber : null;
      const pageSize = isObjectPayload ? payload.PageSize : null;
      const searchText = isObjectPayload ? payload.searchText : "";

      const params = new URLSearchParams();
      if (pageNumber) params.append("PageNumber", pageNumber);
      if (pageSize) params.append("PageSize", pageSize);
      if (searchText) params.append("Search", searchText);

      const queryString = params.toString();
      const url = `${API_URL.WAREHOUSE.BINS_LOOKUP(warehouseId)}${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get(url);
      const responseData = response?.data?.data;

      // Non-paginated response is a plain array (backward compat)
      if (Array.isArray(responseData)) {
        return {
          list: responseData,
          totalPages: 1,
          nextPageNumber: 1,
          appendBins: false,
        };
      }

      // Paginated response shape:
      // { list, pageNumber, pageSize, totalRecords, totalPages, nextPageNumber, hasNextPage }
      return {
        ...(responseData || {}),
        list: responseData?.list || [],
        appendBins: (pageNumber || 1) > 1,
      };
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Get active items for dropdown with pagination support
export const getActiveItems = createAsyncThunk(
  "itemAdjustment/getActiveItems",
  async (payload, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (payload?.searchText) params.append("SearchText", payload.searchText);
      if (payload?.pageNumber) params.append("PageNumber", payload.pageNumber);
      if (payload?.pageSize) params.append("PageSize", payload.pageSize);
      const queryString = params.toString();
      const response = await axios.get(
        `${API_URL.ITEM.ACTIVE_ITEMS_LOOKUP}${queryString ? `?${queryString}` : ""}`,
      );
      // Return both data and append flag
      return {
        ...(response?.data?.data || {}),
        appendItems: payload?.pageNumber > 1, // Append if not first page
      };
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Get item stock for a specific warehouse
export const getItemWarehouseStock = createAsyncThunk(
  "itemAdjustment/getItemWarehouseStock",
  async ({ itemId, warehouseId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.STOCK.ITEM_WAREHOUSE(itemId, warehouseId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Fetch stock breakdown for item (returns multiple rows for bin-managed warehouses)
export const fetchStockBreakdown = createAsyncThunk(
  "itemAdjustment/fetchStockBreakdown",
  async ({ itemId, locationId, warehouseId }, { rejectWithValue }) => {
    try {
      const payload = {
        itemId,
        locationId,
        warehouseId: warehouseId || null,
      };
      const { data } = await axios.post(
        API_URL.STOCK_ADJUSTMENT.STOCK_BREAKDOWN,
        payload,
      );
      return { itemId, breakdown: data.data || [] };
    } catch (error) {
      return rejectWithValue(error?.data ?? error);
    }
  },
);

// Bulk Import Stock Adjustment
export const bulkImportStockAdjustment = createAsyncThunk(
  "itemAdjustment/bulkImportStockAdjustment",
  async (
    { file, locationId, warehouseId, adjustmentReasonId, templateId },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const params = new URLSearchParams();
      params.append("locationId", locationId);
      params.append("warehouseId", warehouseId);
      params.append("adjustmentReasonId", adjustmentReasonId);
      if (templateId) params.append("templateId", templateId);
      const { data } = await axios.post(
        `${API_URL.STOCK_ADJUSTMENT.BULK_IMPORT}?${params.toString()}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data.data;
    } catch (error) {
      if (!error?.data && !error?.response) {
        return rejectWithValue({
          message:
            error?.message ||
            "Network error. The file may be too large or the server timed out.",
          errors: [],
        });
      }
      const errorPayload = error?.data?.data || error?.data || error;
      return rejectWithValue(errorPayload);
    }
  },
);

// Get stock adjustment bulk import history
export const getStockAdjustmentBulkImportHistory = createAsyncThunk(
  "itemAdjustment/getStockAdjustmentBulkImportHistory",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.post(
        `${API_URL.STOCK_ADJUSTMENT.BULK_IMPORT_HISTORY}?${queryString}`,
        {},
        { headers: { "Content-Type": "application/json" } },
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  list: [],
  detail: null,
  current_values: null,
  active_payload: null,
  total_records: 0,
  isLoading: false,
  // Dropdowns
  locations: [],
  warehouses: [],
  bins: [],
  items: [],
  reasons: [],
  // Loading states
  locations_loading: false,
  warehouses_loading: false,
  bins_loading: false,
  items_loading: false,
  reasons_loading: false,
  // Stock breakdown
  stockBreakdown: {},
  // Bulk import history
  bulk_import_history: [],
  stockBreakdown_loading: false,
};

const itemAdjustmentSlice = createSlice({
  name: "itemAdjustment",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.list = [];
    },
    resetDetail(state) {
      state.detail = null;
    },
    resetWarehouses(state) {
      state.warehouses = [];
    },
    resetBins(state) {
      state.bins = [];
    },
    resetItems(state) {
      state.items = [];
    },
    resetStockBreakdown(state) {
      state.stockBreakdown = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Get list
      .addCase(getItemAdjustments.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getItemAdjustments.fulfilled, (state, action) => {
        state.list = action.payload.list;
        state.total_records = action.payload.data?.totalRecords || 0;
        state.isLoading = false;
      })
      .addCase(getItemAdjustments.rejected, (state) => {
        state.isLoading = false;
      })
      // Get by ID
      .addCase(getItemAdjustmentById.fulfilled, (state, action) => {
        state.detail = action.payload;
      })
      // Locations
      .addCase(getActiveLocations.pending, (state) => {
        state.locations_loading = true;
      })
      .addCase(getActiveLocations.fulfilled, (state, action) => {
        state.locations = action.payload || [];
        state.locations_loading = false;
      })
      .addCase(getActiveLocations.rejected, (state) => {
        state.locations_loading = false;
      })
      // Warehouses
      .addCase(getWarehousesByLocation.pending, (state) => {
        state.warehouses_loading = true;
      })
      .addCase(getWarehousesByLocation.fulfilled, (state, action) => {
        state.warehouses = action.payload || [];
        state.warehouses_loading = false;
      })
      .addCase(getWarehousesByLocation.rejected, (state) => {
        state.warehouses_loading = false;
      })
      // Bins
      .addCase(getBinsByWarehouse.pending, (state) => {
        state.bins_loading = true;
      })
      .addCase(getBinsByWarehouse.fulfilled, (state, action) => {
        const newBins = action.payload?.list || [];
        if (action.payload?.appendBins) {
          const existingIds = new Set(state.bins.map((b) => b.binId));
          const uniqueNewBins = newBins.filter(
            (b) => !existingIds.has(b.binId),
          );
          state.bins = [...state.bins, ...uniqueNewBins];
        } else {
          state.bins = newBins;
        }
        state.bins_loading = false;
      })
      .addCase(getBinsByWarehouse.rejected, (state) => {
        state.bins_loading = false;
      })
      // Items
      .addCase(getActiveItems.pending, (state) => {
        state.items_loading = true;
      })
      .addCase(getActiveItems.fulfilled, (state, action) => {
        const newItems = action.payload?.list || [];
        if (action.payload?.appendItems) {
          // Append new items, avoiding duplicates
          const existingIds = new Set(state.items.map((item) => item.id));
          const uniqueNewItems = newItems.filter(
            (item) => !existingIds.has(item.id),
          );
          state.items = [...state.items, ...uniqueNewItems];
        } else {
          // Replace items (first page or new search)
          state.items = newItems;
        }
        state.items_loading = false;
      })
      .addCase(getActiveItems.rejected, (state) => {
        state.items_loading = false;
      })
      // Stock breakdown
      .addCase(fetchStockBreakdown.pending, (state) => {
        state.stockBreakdown_loading = true;
      })
      .addCase(fetchStockBreakdown.fulfilled, (state, action) => {
        state.stockBreakdown[action.payload.itemId] = action.payload.breakdown;
        state.stockBreakdown_loading = false;
      })
      .addCase(fetchStockBreakdown.rejected, (state) => {
        state.stockBreakdown_loading = false;
      })
      // Reasons
      .addCase(getAdjustmentReasons.pending, (state) => {
        state.reasons_loading = true;
      })
      .addCase(getAdjustmentReasons.fulfilled, (state, action) => {
        state.reasons = action.payload || [];
        state.reasons_loading = false;
      })
      .addCase(getAdjustmentReasons.rejected, (state) => {
        state.reasons_loading = false;
      })
      .addCase(
        getStockAdjustmentBulkImportHistory.fulfilled,
        (state, action) => {
          state.bulk_import_history = action.payload.list || [];
        },
      );
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  resetCurrentValue,
  resetDetail,
  resetWarehouses,
  resetBins,
  resetItems,
  resetStockBreakdown,
} = itemAdjustmentSlice.actions;

export default itemAdjustmentSlice.reducer;
